import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Button, Input } from '@agms/ui'
import api, { referenceApi } from '../../lib/api'
import type { AgmsMccBucket, MccOption } from '../../lib/api'
import { apiErrorMessage } from '../../hooks/useErrorHandler'
import type { Merchant } from '../../pages/merchants/useMerchantData'

interface CatalogProcessorField {
  name: string
  label: string
  required: boolean
  min_length?: string | null
  max_length?: string | null
}

interface CatalogProcessor {
  id: string
  name: string
  acquirer_id: string | null
  proc_config_fields: CatalogProcessorField[]
}

interface CatalogService {
  id: string
  name: string
  description: string | null
}

interface AuthorizeNetCatalog {
  market_type_id: number
  fallback: boolean
  processors: CatalogProcessor[]
  services: CatalogService[]
}

export interface AuthorizeNetBoardingDraft {
  business_name: string
  address: string
  city: string
  state: string
  zip: string
  country: string
  phone: string
  email: string
  business_type: string
  tax_id: string
  products_sold: string
  sic_code: string
  market_type_id: string
  age_of_business: string
  owner_name: string
  owner_title: string
  owner_title_note: string
  owner_ssn: string
  reference_id: string
  payment_types: string[]
  value_added_services: string[]
  processor_id: string
  processor_config: Record<string, string>
  name_on_account: string
  bank_account_type: string
  bank_account_owner_type: string
  bank_routing_number: string
  bank_account_number: string
  bank_name: string
  bank_city: string
  bank_state: string
  bank_zip: string
  reseller_rep_name: string
  reseller_rep_id: string
  reseller_rep_commission: string
  has_tax_id: boolean
  tax_id_last4: string | null
  has_owner_ssn: boolean
  owner_ssn_last4: string | null
  has_bank_routing_number: boolean
  has_bank_account_number: boolean
  bank_account_last4: string | null
}

interface AuthorizeNetBoardingWizardProps {
  merchant: Merchant
  onClose: () => void
  onSaved?: () => void
}

interface DeploymentTaskSummary {
  id: string
  status: string
  adapter_type: string
  result_data?: {
    status?: string
    activation_link?: string
  } | null
  last_error?: string | null
}

const MCC_BUCKET_ORDER: AgmsMccBucket[] = [
  'restaurant',
  'retail',
  'supermarket',
  'convenience_store',
  'cnp_moto',
  'ecommerce',
]

const MCC_BUCKET_LABELS: Record<AgmsMccBucket, string> = {
  restaurant: 'Restaurant',
  retail: 'Retail',
  supermarket: 'Supermarket',
  convenience_store: 'Convenience Store',
  cnp_moto: 'CNP / MOTO',
  ecommerce: 'Ecommerce',
}

const AUTHNET_MARKET_TYPES = [
  { value: '0', label: 'E-commerce' },
  { value: '1', label: 'MOTO' },
  { value: '2', label: 'Retail' },
]

const digitsOnly = (value: string) => value.replace(/\D+/g, '')

const EMPTY_DRAFT: AuthorizeNetBoardingDraft = {
  business_name: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  country: 'US',
  phone: '',
  email: '',
  business_type: '',
  tax_id: '',
  products_sold: '',
  sic_code: '',
  market_type_id: '2',
  age_of_business: '',
  owner_name: '',
  owner_title: '',
  owner_title_note: '',
  owner_ssn: '',
  reference_id: '',
  payment_types: ['creditCard'],
  value_added_services: ['8'],
  processor_id: '15',
  processor_config: {},
  name_on_account: '',
  bank_account_type: 'Checking',
  bank_account_owner_type: 'Business',
  bank_routing_number: '',
  bank_account_number: '',
  bank_name: '',
  bank_city: '',
  bank_state: '',
  bank_zip: '',
  reseller_rep_name: '',
  reseller_rep_id: '',
  reseller_rep_commission: '',
  has_tax_id: false,
  tax_id_last4: null,
  has_owner_ssn: false,
  owner_ssn_last4: null,
  has_bank_routing_number: false,
  has_bank_account_number: false,
  bank_account_last4: null,
}

function normalizeDraft(data: Partial<AuthorizeNetBoardingDraft> & Record<string, unknown>, merchant: Merchant): AuthorizeNetBoardingDraft {
  const processorId = String(data.processor_id || EMPTY_DRAFT.processor_id)
  const processorConfig: Record<string, string> = { ...(data.processor_config as Record<string, string> | undefined) }

  if (!Object.keys(processorConfig).length) {
    if (processorId === '15' && data.mid) processorConfig.FDCOMerchantID = String(data.mid)
    if (processorId === '2') {
      if (data.mid) processorConfig.MerchID = String(data.mid)
      if (data.tid) processorConfig.TermID = String(data.tid)
    }
  }

  return {
    ...EMPTY_DRAFT,
    business_name: String(data.business_name || merchant.legal_name || merchant.dba_name || ''),
    phone: String(data.phone || merchant.business_phone || ''),
    email: String(data.email || merchant.business_email || ''),
    address: String(data.address || ''),
    city: String(data.city || ''),
    state: String(data.state || ''),
    zip: String(data.zip || ''),
    country: String(data.country || 'US'),
    business_type: String(data.business_type || ''),
    products_sold: String(data.products_sold || ''),
    sic_code: String(data.sic_code || ''),
    market_type_id: String(data.market_type_id || '2'),
    age_of_business: data.age_of_business === null || data.age_of_business === undefined ? '' : String(data.age_of_business),
    owner_name: String(data.owner_name || ''),
    owner_title: String(data.owner_title || ''),
    owner_title_note: String(data.owner_title_note || ''),
    reference_id: String(data.reference_id || ''),
    payment_types: Array.isArray(data.payment_types) ? data.payment_types.map(String) : ['creditCard'],
    value_added_services: Array.isArray(data.value_added_services) ? data.value_added_services.map(String) : ['8'],
    processor_id: processorId,
    processor_config: processorConfig,
    name_on_account: String(data.name_on_account || ''),
    bank_account_type: String(data.bank_account_type || 'Checking'),
    bank_account_owner_type: String(data.bank_account_owner_type || 'Business'),
    bank_name: String(data.bank_name || ''),
    bank_city: String(data.bank_city || ''),
    bank_state: String(data.bank_state || ''),
    bank_zip: String(data.bank_zip || ''),
    reseller_rep_name: String(data.reseller_rep_name || ''),
    reseller_rep_id: String(data.reseller_rep_id || ''),
    reseller_rep_commission: data.reseller_rep_commission === null || data.reseller_rep_commission === undefined ? '' : String(data.reseller_rep_commission),
    has_tax_id: data.has_tax_id === true,
    tax_id_last4: data.tax_id_last4 ? String(data.tax_id_last4) : null,
    has_owner_ssn: data.has_owner_ssn === true,
    owner_ssn_last4: data.owner_ssn_last4 ? String(data.owner_ssn_last4) : null,
    has_bank_routing_number: data.has_bank_routing_number === true,
    has_bank_account_number: data.has_bank_account_number === true,
    bank_account_last4: data.bank_account_last4 ? String(data.bank_account_last4) : null,
    tax_id: '',
    owner_ssn: '',
    bank_routing_number: '',
    bank_account_number: '',
  }
}

export function AuthorizeNetBoardingWizard({ merchant, onClose, onSaved }: AuthorizeNetBoardingWizardProps) {
  const [step, setStep] = useState(1)
  const [catalog, setCatalog] = useState<AuthorizeNetCatalog | null>(null)
  const [mccOptions, setMccOptions] = useState<MccOption[]>([])
  const [form, setForm] = useState<AuthorizeNetBoardingDraft>(() => normalizeDraft({}, merchant))
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submittedTask, setSubmittedTask] = useState<DeploymentTaskSummary | null>(null)

  useEffect(() => {
    let active = true

    async function load() {
      setLoading(true)
      setError(null)
      try {
        const [catalogResponse, draftResponse, mccOptionsResponse] = await Promise.all([
          api.get<AuthorizeNetCatalog>(`/merchants/${merchant.id}/authorize-net/catalog`),
          api.get<{ data: Record<string, unknown> }>(`/merchants/${merchant.id}/authorize-net/boarding`),
          referenceApi.mccOptions(),
        ])

        if (!active) return

        setCatalog(catalogResponse.data)
        setMccOptions(mccOptionsResponse)
        setForm(normalizeDraft(draftResponse.data.data ?? {}, merchant))
      } catch (err) {
        if (active) setError(apiErrorMessage(err, 'Unable to load Authorize.net boarding wizard.'))
      } finally {
        if (active) setLoading(false)
      }
    }

    load()

    return () => {
      active = false
    }
  }, [merchant])

  const selectedProcessor = useMemo(
    () => catalog?.processors.find((processor) => processor.id === form.processor_id) ?? null,
    [catalog, form.processor_id],
  )

  const groupedMccOptions = useMemo(() => {
    const grouped: Record<AgmsMccBucket, MccOption[]> = {
      restaurant: [],
      retail: [],
      supermarket: [],
      convenience_store: [],
      cnp_moto: [],
      ecommerce: [],
    }

    for (const option of mccOptions) {
      grouped[option.bucket].push(option)
    }

    return grouped
  }, [mccOptions])

  const setField = (field: keyof AuthorizeNetBoardingDraft, value: string) => {
    setForm((current) => ({ ...current, [field]: value }))
  }

  const setProcessorConfig = (field: string, value: string) => {
    setForm((current) => ({
      ...current,
      processor_config: {
        ...current.processor_config,
        [field]: value,
      },
    }))
  }

  const toggleArrayValue = (field: 'payment_types' | 'value_added_services', value: string) => {
    setForm((current) => {
      const values = new Set(current[field])
      if (values.has(value)) values.delete(value)
      else values.add(value)

      return { ...current, [field]: Array.from(values) }
    })
  }

  const validateOwnerSsn = () => {
    if (!form.owner_ssn.trim() && form.has_owner_ssn) return true

    if (digitsOnly(form.owner_ssn).length === 9) return true

    setError('Owner SSN must be the full 9 digits before boarding Authorize.net.')

    return false
  }

  const validateTaxId = () => {
    if (!form.tax_id.trim() && form.has_tax_id) return true

    if (digitsOnly(form.tax_id).length === 9) return true

    setError('Tax ID / EIN must be exactly 9 digits before boarding Authorize.net.')

    return false
  }

  const validateBankAccountNumber = () => {
    if (!form.bank_account_number.trim() && form.has_bank_account_number) return true

    const length = digitsOnly(form.bank_account_number).length
    if (length >= 5 && length <= 17) return true

    setError('Bank account number must be 5 to 17 digits before boarding Authorize.net.')

    return false
  }

  const validateAgeOfBusiness = () => {
    const age = Number(form.age_of_business || 0)
    if (Number.isInteger(age) && age >= 0 && age <= 255) return true

    setError('Age of business must be a whole number from 0 to 255.')

    return false
  }

  const validateCurrentStep = () => {
    if (step === 1) return validateTaxId() && validateOwnerSsn() && validateAgeOfBusiness()
    if (step === 3) return validateBankAccountNumber()

    return true
  }

  const buildPayload = () => {
    const valueAddedServices = new Set(form.value_added_services)
    if (form.payment_types.includes('eCheck')) {
      valueAddedServices.add('4')
    }

    const payload: Record<string, unknown> = {
        ...form,
        value_added_services: Array.from(valueAddedServices),
        state: form.state.toUpperCase(),
        country: form.country.toUpperCase(),
        bank_state: form.bank_state.toUpperCase(),
        tax_id: digitsOnly(form.tax_id),
        owner_ssn: digitsOnly(form.owner_ssn),
        bank_account_number: digitsOnly(form.bank_account_number),
        market_type_id: Number(form.market_type_id || 2),
        age_of_business: Number(form.age_of_business || 0),
        reseller_rep_commission: form.reseller_rep_commission.trim() || null,
      }

      for (const field of ['tax_id', 'owner_ssn', 'bank_routing_number', 'bank_account_number'] as const) {
        if (!form[field].trim()) {
          delete payload[field]
        }
      }

      for (const field of ['has_tax_id', 'tax_id_last4', 'has_owner_ssn', 'owner_ssn_last4', 'has_bank_routing_number', 'has_bank_account_number', 'bank_account_last4'] as const) {
        delete payload[field]
      }

    return payload
  }

  const submitBoarding = async () => {
    setSaving(true)
    setError(null)
    setSubmittedTask(null)

    try {
      const payload = buildPayload()
      await api.put(`/merchants/${merchant.id}/authorize-net/boarding`, payload)
      const response = await api.post<DeploymentTaskSummary>(`/merchants/${merchant.id}/authorize-net/boarding/submit`)
      setSubmittedTask(response.data)
      onSaved?.()
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to board Authorize.net.'))
    } finally {
      setSaving(false)
    }
  }

  const nextLabel = step === 3 ? 'Board Authorize.net' : 'Next'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4">
      <div className="max-h-[92vh] w-full max-w-5xl overflow-y-auto rounded-lg bg-white shadow-xl">
        <div className="border-b border-gray-200 px-5 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Authorize.net boarding draft for {merchant.dba_name}</h2>
          <p className="mt-1 text-sm text-gray-500">
            Final submission queues sandbox Authorize.net boarding and requests an activation link when the vendor account is created.
          </p>
          <div className="mt-3 flex gap-2 text-xs font-semibold">
            {[1, 2, 3].map((item) => (
              <span key={item} className={`rounded-full border px-3 py-1 ${step === item ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-gray-200 bg-gray-50 text-gray-600'}`}>
                Step {item}
              </span>
            ))}
          </div>
        </div>

        <form
          className="space-y-5 px-5 py-4"
          onSubmit={(event) => {
            event.preventDefault()
            setError(null)
            if (!validateCurrentStep()) return
            if (step < 3) setStep(step + 1)
            else void submitBoarding()
          }}
        >
          {loading && <p className="py-8 text-center text-sm text-gray-500">Loading Authorize.net catalog...</p>}
          {error && (
            <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
              {error}
            </p>
          )}
          {submittedTask && (
            <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
              <p className="font-semibold">Authorize.net provisioning queued</p>
              <p className="mt-1">Deployment task {submittedTask.id} is {submittedTask.status.replace(/_/g, ' ')}.</p>
              {submittedTask.result_data?.status && (
                <p className="mt-1">Vendor status: {submittedTask.result_data.status.replace(/_/g, ' ')}</p>
              )}
              {submittedTask.result_data?.activation_link && (
                <a className="mt-1 inline-block font-semibold text-blue-700 underline" href={submittedTask.result_data.activation_link} target="_blank" rel="noreferrer">
                  Open activation link
                </a>
              )}
              {submittedTask.last_error && (
                <p className="mt-1 text-rose-800">{submittedTask.last_error}</p>
              )}
            </div>
          )}

          {!loading && (
            <>
              {step === 1 && (
                <StepOne form={form} groupedMccOptions={groupedMccOptions} setField={setField} />
              )}

              {step === 2 && catalog && (
                <StepTwo
                  catalog={catalog}
                  form={form}
                  selectedProcessor={selectedProcessor}
                  setField={setField}
                  setProcessorConfig={setProcessorConfig}
                  toggleArrayValue={toggleArrayValue}
                />
              )}

              {step === 3 && (
                <StepThree form={form} setField={setField} />
              )}
            </>
          )}

          <div className="flex justify-between gap-2 border-t border-gray-200 pt-4">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <div className="flex gap-2">
              {step > 1 && (
                <Button type="button" variant="secondary" onClick={() => setStep(step - 1)}>
                  Back
                </Button>
              )}
              <Button type="submit" disabled={loading || saving}>
                {saving ? (step === 3 ? 'Boarding...' : 'Saving...') : nextLabel}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

function StepOne({
  form,
  groupedMccOptions,
  setField,
}: {
  form: AuthorizeNetBoardingDraft
  groupedMccOptions: Record<AgmsMccBucket, MccOption[]>
  setField: (field: keyof AuthorizeNetBoardingDraft, value: string) => void
}) {
  const savedSicExists = Object.values(groupedMccOptions)
    .flat()
    .some((option) => (option.sic || option.mcc) === form.sic_code)
  const showSavedManualCode = form.sic_code !== '' && /^\d{1,4}$/.test(form.sic_code) && !savedSicExists

  return (
    <div className="space-y-5">
      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-900">Business</h3>
        <div className="grid gap-4 md:grid-cols-2">
          <TextField label="Business name" value={form.business_name} onChange={(value) => setField('business_name', value)} required />
          <TextField label="Business phone" value={form.phone} onChange={(value) => setField('phone', value)} required />
          <TextField label="Business email" type="email" value={form.email} onChange={(value) => setField('email', value)} required />
          <TextField label="Reference ID" value={form.reference_id} maxLength={20} onChange={(value) => setField('reference_id', value)} />
        </div>
        <TextField label="Business address" value={form.address} onChange={(value) => setField('address', value)} required />
        <div className="grid gap-4 md:grid-cols-4">
          <TextField label="Business city" value={form.city} onChange={(value) => setField('city', value)} required />
          <TextField label="Business state" value={form.state} maxLength={2} onChange={(value) => setField('state', value.toUpperCase())} required />
          <TextField label="Business ZIP" value={form.zip} onChange={(value) => setField('zip', value)} required />
          <TextField label="Country" value={form.country} maxLength={2} onChange={(value) => setField('country', value.toUpperCase())} required />
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-900">Business info</h3>
        <div className="grid gap-4 md:grid-cols-3">
          <SelectField label="Business type" value={form.business_type} onChange={(value) => setField('business_type', value)} required>
            <option value="">Select...</option>
            <option value="SoleProprietorShip">Sole Proprietorship</option>
            <option value="PartnerShip">Partnership</option>
            <option value="Corporation">Corporation</option>
            <option value="Corporation">LLC (files as Corporation)</option>
            <option value="NonProfit">Non-Profit</option>
          </SelectField>
          <TextField
            label="Tax ID / EIN"
            value={form.tax_id}
            onChange={(value) => setField('tax_id', value)}
            required={!form.has_tax_id}
            helperText={form.has_tax_id ? `•••• ${form.tax_id_last4 ?? 'set'} (saved)` : 'Enter the 9-digit EIN. Dashes and spaces are OK.'}
          />
          <TextField label="Products sold" value={form.products_sold} onChange={(value) => setField('products_sold', value)} required />
          <SelectField label="Industry Type" value={form.sic_code} onChange={(value) => setField('sic_code', value)} required>
            <option value="">Select...</option>
            {showSavedManualCode && (
              <option value={form.sic_code}>Saved code ({form.sic_code})</option>
            )}
            {MCC_BUCKET_ORDER.map((bucket) => {
              const options = groupedMccOptions[bucket]
              if (options.length === 0) return null

              return (
                <optgroup key={bucket} label={MCC_BUCKET_LABELS[bucket]}>
                  {options.map((option) => {
                    const value = option.sic || option.mcc

                    return (
                      <option key={`${option.mcc}-${value}`} value={value}>
                        {option.label} ({value})
                      </option>
                    )
                  })}
                </optgroup>
              )
            })}
          </SelectField>
          <SelectField label="Market Type" value={form.market_type_id} onChange={(value) => setField('market_type_id', value)} required>
            {AUTHNET_MARKET_TYPES.map((marketType) => (
              <option key={marketType.value} value={marketType.value}>
                {marketType.label}
              </option>
            ))}
          </SelectField>
          <TextField label="Age of business" type="number" value={form.age_of_business} onChange={(value) => setField('age_of_business', value)} required />
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-900">Owner</h3>
        <div className="grid gap-4 md:grid-cols-3">
          <TextField label="Owner name" value={form.owner_name} onChange={(value) => setField('owner_name', value)} required />
          <SelectField label="Owner title" value={form.owner_title} onChange={(value) => setField('owner_title', value)} required>
            <option value="">Select...</option>
            <option value="President">President</option>
            <option value="Owner">Owner</option>
            <option value="Manager">Manager</option>
            <option value="Other">Other</option>
          </SelectField>
          <TextField
            label="Owner SSN — 9 digits"
            value={form.owner_ssn}
            onChange={(value) => setField('owner_ssn', value)}
            required={!form.has_owner_ssn}
            helperText={form.has_owner_ssn ? `SSN on file${form.owner_ssn_last4 ? ` •••• ${form.owner_ssn_last4}` : ''} (saved)` : 'Enter the full SSN. Dashes and spaces are OK.'}
          />
        </div>
        {form.owner_title === 'Other' && (
          <TextField label="Note for other title" value={form.owner_title_note} onChange={(value) => setField('owner_title_note', value)} required />
        )}
      </section>
    </div>
  )
}

function StepTwo({
  catalog,
  form,
  selectedProcessor,
  setField,
  setProcessorConfig,
  toggleArrayValue,
}: {
  catalog: AuthorizeNetCatalog
  form: AuthorizeNetBoardingDraft
  selectedProcessor: CatalogProcessor | null
  setField: (field: keyof AuthorizeNetBoardingDraft, value: string) => void
  setProcessorConfig: (field: string, value: string) => void
  toggleArrayValue: (field: 'payment_types' | 'value_added_services', value: string) => void
}) {
  return (
    <div className="space-y-5">
      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-900">Payment methods</h3>
        <div className="grid gap-3 md:grid-cols-2">
          <CheckboxField label="Credit Card" checked={form.payment_types.includes('creditCard')} onChange={() => toggleArrayValue('payment_types', 'creditCard')} />
          <CheckboxField label="eCheck" checked={form.payment_types.includes('eCheck')} onChange={() => toggleArrayValue('payment_types', 'eCheck')} />
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-900">Value-added services</h3>
        <div className="grid gap-3 md:grid-cols-2">
          {catalog.services.map((service) => (
            <CheckboxField
              key={service.id}
              label={`${service.id} - ${service.name}`}
              checked={form.value_added_services.includes(service.id)}
              onChange={() => toggleArrayValue('value_added_services', service.id)}
            />
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-900">Processor</h3>
        <SelectField
          label="Processor"
          value={form.processor_id}
          onChange={(value) => setField('processor_id', value)}
          required
        >
          {catalog.processors.map((processor) => (
            <option key={processor.id} value={processor.id}>{processor.name}</option>
          ))}
        </SelectField>

        <div className="grid gap-4 md:grid-cols-2">
          {selectedProcessor?.proc_config_fields.map((field) => (
            <TextField
              key={field.name}
              label={field.label}
              value={form.processor_config[field.name] || ''}
              onChange={(value) => setProcessorConfig(field.name, value)}
              required
            />
          ))}
        </div>
      </section>
    </div>
  )
}

function StepThree({ form, setField }: { form: AuthorizeNetBoardingDraft; setField: (field: keyof AuthorizeNetBoardingDraft, value: string) => void }) {
  return (
    <div className="space-y-5">
      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-900">Bank info</h3>
        <div className="grid gap-4 md:grid-cols-3">
          <TextField label="Name on account" value={form.name_on_account} onChange={(value) => setField('name_on_account', value)} required />
          <SelectField label="Account type" value={form.bank_account_type} onChange={(value) => setField('bank_account_type', value)} required>
            <option value="Checking">Checking</option>
            <option value="Savings">Savings</option>
          </SelectField>
          <SelectField label="Account owner type" value={form.bank_account_owner_type} onChange={(value) => setField('bank_account_owner_type', value)} required>
            <option value="Business">Business</option>
            <option value="Personal">Personal</option>
          </SelectField>
          <TextField
            label="ABA routing"
            value={form.bank_routing_number}
            onChange={(value) => setField('bank_routing_number', value.replace(/\D+/g, ''))}
            required={!form.has_bank_routing_number}
            helperText={form.has_bank_routing_number ? 'Routing number on file (saved)' : undefined}
          />
          <TextField
            label="Account number"
            value={form.bank_account_number}
            onChange={(value) => setField('bank_account_number', value)}
            required={!form.has_bank_account_number}
            helperText={form.has_bank_account_number ? `•••• ${form.bank_account_last4 ?? 'set'} (saved)` : 'Enter 5 to 17 digits. Spaces and dashes are OK.'}
          />
          <TextField label="Bank name" value={form.bank_name} onChange={(value) => setField('bank_name', value)} required />
          <TextField label="Bank city" value={form.bank_city} onChange={(value) => setField('bank_city', value)} required />
          <TextField label="Bank state" value={form.bank_state} maxLength={2} onChange={(value) => setField('bank_state', value.toUpperCase())} required />
          <TextField label="Bank ZIP" value={form.bank_zip} onChange={(value) => setField('bank_zip', value)} required />
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-900">Reseller rep</h3>
        <div className="grid gap-4 md:grid-cols-3">
          <TextField label="Reseller rep name" value={form.reseller_rep_name} onChange={(value) => setField('reseller_rep_name', value)} />
          <TextField label="Reseller rep ID" value={form.reseller_rep_id} onChange={(value) => setField('reseller_rep_id', value)} />
          <TextField label="Reseller rep commission" type="number" value={form.reseller_rep_commission} onChange={(value) => setField('reseller_rep_commission', value)} />
        </div>
      </section>
    </div>
  )
}

function TextField({
  label,
  value,
  onChange,
  type = 'text',
  required = false,
  maxLength,
  helperText,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  type?: string
  required?: boolean
  maxLength?: number
  helperText?: string
}) {
  const id = label.toLowerCase().replace(/[^a-z0-9]+/g, '-')

  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor={id}>
        {label}
      </label>
      <Input
        id={id}
        type={type}
        value={value}
        maxLength={maxLength}
        onChange={(event) => onChange(event.target.value)}
        required={required}
      />
      {helperText && (
        <p className="mt-1 text-xs text-gray-500">{helperText}</p>
      )}
    </div>
  )
}

function SelectField({
  label,
  value,
  onChange,
  required = false,
  children,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  required?: boolean
  children: ReactNode
}) {
  const id = label.toLowerCase().replace(/[^a-z0-9]+/g, '-')

  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        className="block h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        required={required}
      >
        {children}
      </select>
    </div>
  )
}

function CheckboxField({ label, checked, onChange }: { label: string; checked: boolean; onChange: () => void }) {
  const id = label.toLowerCase().replace(/[^a-z0-9]+/g, '-')

  return (
    <label htmlFor={id} className="flex items-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700">
      <input
        id={id}
        type="checkbox"
        className="h-4 w-4 rounded border-gray-300 text-blue-600"
        checked={checked}
        onChange={onChange}
      />
      <span>{label}</span>
    </label>
  )
}
